import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  sectionHeaderVariants,
  type SectionHeaderVariantProps,
} from './section-header.variants';
import { Brow } from '@/hen-ui/atomics/Brow';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Button, type ButtonProps } from '@/hen-ui/atomics/Button';
import { Icon, type IconProps, type IconName } from '@/hen-ui/atomics/Icon';
import { Flex } from '@/hen-ui/composites/Flex';

export interface ActionButtonConfig
  extends Omit<ButtonProps, 'variant' | 'size'> {
  text: string;
}

export interface ButtonConfig extends Omit<ButtonProps, 'children'> {
  text: string;
}

/**
 * Accessibility attributes for the leading `Icon` (use `iconSize` / `iconColor` for visuals).
 * `aria-hidden` follows React `Booleanish` (boolean or `'true'` / `'false'`), matching SVG/DOM.
 * Do not use `className` on `Icon` via this API; use `iconSize` / `iconColor`.
 */
export type SectionHeaderIconProps = {
  'aria-hidden'?: React.AriaAttributes['aria-hidden'];
  'aria-label'?: IconProps['aria-label'];
  role?: IconProps['role'];
};

export interface SectionHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    SectionHeaderVariantProps {
  /**
   * Heading size - required
   */
  size: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Main heading text - required
   */
  title: string;
  /** Optional className merged onto the title Heading (e.g. line clamp) */
  titleClassName?: string;
  /**
   * Purple eyebrow text above title - optional
   */
  brow?: string;
  /**
   * Description paragraph below title - optional
   */
  subtext?: string;
  /**
   * Text link button config - renders on right side (left-aligned) or stacked (center)
   */
  actionButton?: ActionButtonConfig;
  /**
   * Primary CTA button config - renders below subtext
   */
  button?: ButtonConfig;
  /**
   * Text/icon color: default (neutral) or white
   */
  color?: 'default' | 'white';
  /** Optional Lucide icon rendered to the left of the title (decorative by default). */
  icon?: IconName;
  /** Icon size; defaults from heading level (h1/h2 → lg, h3/h4 → md, h5/h6 → sm). */
  iconSize?: IconProps['size'];
  /**
   * Icon color token; defaults from section `color`. When omitted on a default
   * section, the icon uses the heading token; pass `iconColor="default"` explicitly
   * for Icon’s `text-current` default instead.
   */
  iconColor?: IconProps['color'];
  /**
   * A11y attributes for the leading `Icon`. Non-empty `aria-label` defaults to
   * `aria-hidden={false}` so the label is exposed (override with explicit `aria-hidden`).
   */
  iconProps?: SectionHeaderIconProps;
}

function defaultIconSizeForHeading(
  headingSize: SectionHeaderProps['size'],
): NonNullable<IconProps['size']> {
  if (headingSize === 'h1' || headingSize === 'h2') {
    return 'lg';
  }
  if (headingSize === 'h3' || headingSize === 'h4') {
    return 'md';
  }
  return 'sm';
}

function sectionHeaderColorToIconColor(
  sectionColor: NonNullable<SectionHeaderProps['color']>,
): NonNullable<IconProps['color']> {
  return sectionColor === 'white' ? 'white' : 'default';
}

/**
 * Map `aria-hidden` to boolean. Handles React `Booleanish` at runtime so string
 * `'false'` is not truthy.
 */
function normalizeAriaHidden(
  value: NonNullable<React.AriaAttributes['aria-hidden']>,
): boolean {
  if (value === false || value === 'false') {
    return false;
  }
  if (value === true || value === 'true') {
    return true;
  }
  return Boolean(value);
}

/** Decorative by default; non-empty `aria-label` implies `aria-hidden={false}` unless overridden. */
function resolveIconAccessibility(
  iconProps?: SectionHeaderIconProps,
): Pick<SectionHeaderIconProps, 'aria-hidden' | 'aria-label' | 'role'> {
  const hiddenProp = iconProps?.['aria-hidden'];
  const labelProp = iconProps?.['aria-label'];
  const role = iconProps?.role;
  const hasMeaningfulLabel =
    labelProp != null && String(labelProp).trim().length > 0;
  const ariaHidden =
    hiddenProp != null
      ? normalizeAriaHidden(hiddenProp)
      : hasMeaningfulLabel
        ? false
        : true;
  return {
    'aria-hidden': ariaHidden,
    ...(hasMeaningfulLabel ? { 'aria-label': labelProp } : {}),
    ...(role !== undefined ? { role } : {}),
  };
}

/**
 * SectionHeader component following HEN Design System standards.
 *
 * A flexible section header that displays a heading with optional eyebrow, subtext,
 * action button, and CTA button. Supports both left-aligned and centered layouts.
 *
 * @example
 * // Basic left-aligned header
 * <SectionHeader
 *   size="h2"
 *   title="Section Title"
 * />
 *
 * @example
 * // Full left-aligned header with action button
 * <SectionHeader
 *   size="h2"
 *   align="left"
 *   brow="Eyebrow Text"
 *   title="Section Title"
 *   subtext="This is a description paragraph that provides context."
 *   actionButton={{ text: "View all", href: "/all" }}
 * />
 *
 * @example
 * // Centered header with CTA
 * <SectionHeader
 *   size="h1"
 *   align="center"
 *   brow="Welcome"
 *   title="Get Started Today"
 *   subtext="Join thousands of users already using our platform."
 *   button={{ text: "Sign Up", variant: "primaryFill" }}
 * />
 *
 * @example
 * // Title with leading icon (Salary Comparison style)
 * <SectionHeader
 *   size="h2"
 *   title="Recommended for You"
 *   icon="check-circle"
 * />
 */
export const SectionHeader = React.forwardRef<
  HTMLDivElement,
  SectionHeaderProps
>(
  (
    {
      className,
      size,
      align = 'left',
      'align-md': alignMd,
      title,
      titleClassName,
      brow,
      subtext,
      color = 'default',
      actionButton,
      button,
      icon,
      iconSize,
      iconColor,
      iconProps,
      ...props
    },
    ref,
  ) => {
    const isLeftAligned = align === 'left';

    let actionButtonProps: Omit<ActionButtonConfig, 'text'> | undefined;
    let actionButtonLabel: string | undefined;
    if (actionButton) {
      const { text, ...rest } = actionButton;
      actionButtonLabel = text;
      actionButtonProps = rest;
    }

    let primaryButtonProps: Omit<ButtonConfig, 'text'> | undefined;
    let primaryButtonLabel: string | undefined;
    if (button) {
      const { text, ...rest } = button;
      primaryButtonLabel = text;
      primaryButtonProps = rest;
    }

    let actionButtonNode: React.ReactNode = null;
    if (actionButtonProps) {
      if (actionButtonProps.href) {
        const { href, ...anchorActionButtonProps } = actionButtonProps;
        actionButtonNode = (
          <Button
            variant='textOnly'
            size='sm'
            {...anchorActionButtonProps}
            href={href}
          >
            {actionButtonLabel}
          </Button>
        );
      } else {
        const {
          href: _href,
          target: _target,
          rel: _rel,
          ...buttonOnlyActionButtonProps
        } = actionButtonProps;
        actionButtonNode = (
          <Button variant='textOnly' size='sm' {...buttonOnlyActionButtonProps}>
            {actionButtonLabel}
          </Button>
        );
      }
    }

    let primaryButtonNode: React.ReactNode = null;
    if (primaryButtonProps) {
      if (primaryButtonProps.href) {
        const { href, ...anchorPrimaryButtonProps } = primaryButtonProps;
        primaryButtonNode = (
          <Button {...anchorPrimaryButtonProps} href={href}>
            {primaryButtonLabel}
          </Button>
        );
      } else {
        const {
          href: _href,
          target: _target,
          rel: _rel,
          ...buttonOnlyPrimaryButtonProps
        } = primaryButtonProps;
        primaryButtonNode = (
          <Button {...buttonOnlyPrimaryButtonProps}>
            {primaryButtonLabel}
          </Button>
        );
      }
    }

    const titleGroupClassName =
      isLeftAligned && actionButton ? 'min-w-0 flex-1' : undefined;

    // With a leading icon, the title uses flex-1 + min-w-0 so it can shrink; with only an action link, use `titleGroupClassName` instead.
    const titleHeading = (
      <Heading
        variant={size}
        color={color}
        className={cn(
          titleClassName,
          icon ? 'min-w-0 flex-1' : titleGroupClassName,
        )}
      >
        {title}
      </Heading>
    );

    const resolvedIconColor = iconColor ?? sectionHeaderColorToIconColor(color);
    /** Match heading token only when consumers omit `iconColor`; explicit `default` uses Icon’s text-current. */
    const iconInheritsHeadingColor =
      color === 'default' && iconColor === undefined;

    const iconNode =
      icon !== undefined ? (
        <Icon
          icon={icon}
          {...resolveIconAccessibility(iconProps)}
          size={iconSize ?? defaultIconSizeForHeading(size)}
          color={iconInheritsHeadingColor ? 'heading' : resolvedIconColor}
        />
      ) : null;

    const titleRowNeedsFlex =
      Boolean(icon) || Boolean(isLeftAligned && actionButton);

    const titleRowContent =
      iconNode !== null ? (
        <Flex
          direction='row'
          align='center'
          gap='sm'
          className={titleGroupClassName}
        >
          {iconNode}
          {titleHeading}
        </Flex>
      ) : (
        titleHeading
      );

    return (
      <Flex
        ref={ref}
        gap='xxs'
        className={cn(
          sectionHeaderVariants({ align, 'align-md': alignMd }),
          className,
        )}
        direction='column'
        justify='between'
        align='start'
        {...props}
      >
        {/* Brow */}
        {brow && <Brow color={color}>{brow}</Brow>}

        {/* Title row: optional icon + heading; left + actionButton keeps link on the right */}
        {titleRowNeedsFlex ? (
          <Flex
            direction='row'
            justify={
              isLeftAligned && actionButton
                ? 'between'
                : isLeftAligned
                  ? 'start'
                  : 'center'
            }
            align='center'
            className='w-full'
          >
            {isLeftAligned && actionButton ? (
              <>
                {titleRowContent}
                {actionButtonNode}
              </>
            ) : (
              titleRowContent
            )}
          </Flex>
        ) : (
          titleHeading
        )}

        {/* Subtext */}
        {subtext && (
          <Text variant='b4' color={color === 'white' ? 'white' : 'subtext'}>
            {subtext}
          </Text>
        )}

        {/* Primary CTA button */}
        {button && primaryButtonNode}
      </Flex>
    );
  },
);

SectionHeader.displayName = 'SectionHeader';
